import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { QuickAddSupplier, QuickAddQuality, QuickAddSubQuality } from '../components/ui/quick-add';

const UNIT_OPTIONS = ['Meters', 'Bales', 'Thans', 'Kg', 'Pieces'];

const EditGreyOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [subqualities, setSubqualities] = useState([]);
  const [allSubqualities, setAllSubqualities] = useState([]);
  const [items, setItems] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showAddQuality, setShowAddQuality] = useState(false);
  const [showAddSubQuality, setShowAddSubQuality] = useState(false);
  
  const [formData, setFormData] = useState({
    order_date: '',
    quality_id: '',
    subquality_id: '',
    item_id: '',
    item: '',
    supplier_id: '',
    city: '',
    rate: '',
    order_unit: '',
    ordered_quantity: '',
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, [orderId]);

  const fetchData = async () => {
    try {
      const [orderRes, suppliersRes, qualitiesRes, subqualitiesRes, itemsRes] = await Promise.all([
        api.get(`/grey-orders/${orderId}`),
        api.get('/suppliers'),
        api.get('/qualities'),
        api.get('/subqualities'),
        api.get('/items')
      ]);
      
      const order = orderRes.data;
      setFormData({
        order_date: order.order_date.split('T')[0],
        quality_id: order.quality_id,
        subquality_id: order.subquality_id,
        item_id: order.item_id || '',
        item: order.item || '',
        supplier_id: order.supplier_id,
        city: order.city,
        rate: order.rate.toString(),
        order_unit: order.order_unit,
        ordered_quantity: order.ordered_quantity.toString(),
        remarks: order.remarks || ''
      });
      
      setSuppliers(suppliersRes.data);
      setQualities(qualitiesRes.data);
      setAllSubqualities(subqualitiesRes.data);
      setItems(itemsRes.data);
      
      const filtered = subqualitiesRes.data.filter(sq => (sq.quality_ids?.length ? sq.quality_ids : [sq.quality_id]).includes(order.quality_id));
      setSubqualities(filtered);
      
      setLoadingData(false);
    } catch (error) {
      toast.error('Failed to load order data');
      setLoadingData(false);
    }
  };

  const handleQualityChange = async (qualityId) => {
    setFormData({ ...formData, quality_id: qualityId, subquality_id: '', order_unit: '' });
    
    const filtered = allSubqualities.filter(sq => (sq.quality_ids?.length ? sq.quality_ids : [sq.quality_id]).includes(qualityId));
    setSubqualities(filtered);
    
    try {
      const qualityRes = await api.get(`/qualities/${qualityId}`);
      const quality = qualityRes.data;
      if (quality.default_unit) {
        setFormData(prev => ({ ...prev, quality_id: qualityId, order_unit: quality.default_unit }));
        toast.success(`Unit auto-populated: ${quality.default_unit}`);
      }
    } catch {
      toast.error('Could not auto-populate unit. Please select manually.');
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.supplier_id === supplierId);
    setFormData({
      ...formData,
      supplier_id: supplierId,
      city: supplier?.city || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.item_id) { toast.error('Item is required'); return; }
    setLoading(true);

    try {
      const payload = {
        ...formData,
        rate: parseFloat(formData.rate),
        ordered_quantity: parseFloat(formData.ordered_quantity)
      };

      await api.put(`/grey-orders/${orderId}`, payload);
      toast.success('Order updated successfully!');
      navigate(`/grey-orders/${orderId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/grey-orders/${orderId}`)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Order
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Edit Grey Order
        </h1>
        <p className="text-slate-600 mt-1">Update order details</p>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order_date">Order Date *</Label>
                <Input
                  id="order_date"
                  type="date"
                  value={formData.order_date}
                  onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
              <div></div>
            </div>

            {/* Supplier and City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="supplier" className="flex items-center justify-between">
                  <span>Supplier *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddSupplier(true)}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={handleSupplierChange}
                  required
                >
                  <SelectTrigger data-testid="edit-supplier-select" className="h-11">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                        {supplier.name} - {supplier.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  type="text"
                  data-testid="edit-city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            </div>

            {/* Item */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="item">Item *</Label>
                <Select
                  value={formData.item_id}
                  onValueChange={(itemId) => {
                    const item = items.find(i => i.item_id === itemId);
                    setFormData({ ...formData, item_id: itemId, item: item?.item_name || '' });
                  }}
                >
                  <SelectTrigger data-testid="edit-item-select" className="h-11">
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.item_id} value={item.item_id}>
                        {item.item_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div></div>
            </div>

            {/* Quality and Finish Quality */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quality" className="flex items-center justify-between">
                  <span>Quality *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddQuality(true)}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.quality_id}
                  onValueChange={handleQualityChange}
                  required
                >
                  <SelectTrigger data-testid="edit-quality-select" className="h-11">
                    <SelectValue placeholder="Select quality" />
                  </SelectTrigger>
                  <SelectContent>
                    {qualities.map((quality) => (
                      <SelectItem key={quality.quality_id} value={quality.quality_id}>
                        {quality.quality_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subquality" className="flex items-center justify-between">
                  <span>Finish Quality *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700" onClick={() => setShowAddSubQuality(true)} disabled={!formData.quality_id}>
                    <Plus className="h-3 w-3 mr-1" />Add New
                  </Button>
                </Label>
                <Select
                  value={formData.subquality_id}
                  onValueChange={(value) => setFormData({ ...formData, subquality_id: value })}
                  required
                  disabled={!formData.quality_id}
                >
                  <SelectTrigger data-testid="edit-finish-quality-select" className="h-11">
                    <SelectValue placeholder="Select finish quality" />
                  </SelectTrigger>
                  <SelectContent>
                    {subqualities.map((subquality) => (
                      <SelectItem key={subquality.subquality_id} value={subquality.subquality_id}>
                        {subquality.subquality_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="order_unit">Unit *</Label>
                <Select
                  value={formData.order_unit}
                  onValueChange={(value) => setFormData({ ...formData, order_unit: value })}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ordered_quantity">Ordered Quantity *</Label>
                <Input
                  id="ordered_quantity"
                  type="number"
                  step="0.01"
                  value={formData.ordered_quantity}
                  onChange={(e) => setFormData({ ...formData, ordered_quantity: e.target.value })}
                  required
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="rate">Rate per Unit *</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea
                id="remarks"
                data-testid="edit-remarks-input"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
              />
            </div>

            {/* Summary */}
            {formData.ordered_quantity && formData.rate && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Order Value</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  ₹ {(() => {
                    const qty = parseFloat(formData.ordered_quantity || 0);
                    const rate = parseFloat(formData.rate || 0);
                    const unit = formData.order_unit;
                    const conversionFactors = { 'Meters': 1, 'Thans': 140, 'Bales': 980 };
                    const factor = conversionFactors[unit] || 1;
                    return (qty * factor * rate).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                  })()}
                </p>
                {formData.order_unit && formData.order_unit !== 'Meters' && ['Thans', 'Bales'].includes(formData.order_unit) && (
                  <>
                    <p className="text-sm text-amber-600 mt-1 font-medium">≈ Approximately</p>
                    <p className="text-xs text-slate-500 mt-1">
                      ({(() => {
                        const qty = parseFloat(formData.ordered_quantity || 0);
                        const conversionFactors = { 'Thans': 140, 'Bales': 980 };
                        return (qty * (conversionFactors[formData.order_unit] || 1)).toLocaleString('en-IN');
                      })()} meters @ ₹{formData.rate}/meter)
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/grey-orders/${orderId}`)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="update-order-btn"
                type="submit"
                disabled={loading}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Order'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>

      <QuickAddSupplier
        open={showAddSupplier}
        onOpenChange={setShowAddSupplier}
        onCreated={(newSupplier) => {
          setSuppliers(prev => [...prev, newSupplier]);
          handleSupplierChange(newSupplier.supplier_id);
        }}
      />
      <QuickAddQuality
        open={showAddQuality}
        onOpenChange={setShowAddQuality}
        onCreated={(newQuality) => {
          setQualities(prev => [...prev, newQuality]);
          handleQualityChange(newQuality.quality_id);
        }}
      />
      <QuickAddSubQuality
        open={showAddSubQuality}
        onOpenChange={setShowAddSubQuality}
        qualities={qualities}
        defaultQualityId={formData.quality_id}
        onCreated={(newSQ) => {
          setAllSubqualities(prev => [...prev, newSQ]);
          const parents = newSQ.quality_ids?.length ? newSQ.quality_ids : [newSQ.quality_id];
          if (parents.includes(formData.quality_id)) {
            setSubqualities(prev => [...prev, newSQ]);
          }
          setFormData(prev => ({ ...prev, subquality_id: newSQ.subquality_id }));
        }}
      />
    </>
  );
};

export default EditGreyOrder;
